<template>
  <section>
    <div class="titulo">
      <div class="margem container">
        <div class="linha alinha-v">
          <div class="m-icone esquerda"><a @click="$router.back()" style="cursor: pointer" class="icone-voltar m-d"></a>
          </div>
          <h2>Agendar visita</h2>
        </div>
      </div>
    </div>
    <div class="margem container">
      <div class="bloco margem agenda-cal-wrap">
        <div class="agenda-cal-filtros">
          <div v-if="permissoes.includes(256)" class="agenda-cal-filtro">
            <label class="agenda-cal-filtro-label" for="filtro-setor-cal">Área ou setor a ser visitado</label>
            <BuscaAutocomplete
              input-id="filtro-setor-cal"
              v-model="filtroSetorNome"
              :options="setoresLista"
              value-key="nome"
              :label-fn="rotuloSetorSelect"
              :style-fn="estiloOptionSetor"
              input-class="agenda-cal-filtro-input"
              empty-label="Todos"
              placeholder="Buscar setor…"
              :loading="setoresCarregando"
              loading-label="Carregando setores…"
              :disabled="setoresCarregando"
            />
          </div>
          <div class="agenda-cal-filtro agenda-cal-filtro--pesquisa-visitante">
            <label class="agenda-cal-filtro-label" for="filtro-visitante-cal">Pesquisar visitante</label>
            <div class="agenda-cal-pesquisa-row">
              <input
                id="filtro-visitante-cal"
                v-model.trim="filtroNomeVisitanteCal"
                type="search"
                class="agenda-cal-filtro-input agenda-cal-filtro-input--com-botao"
                placeholder="Nome ou documento (CPF/RG)"
                autocomplete="off"
                :disabled="pesquisandoVisitanteEnter"
                @keydown.enter.prevent="executarPesquisaVisitanteEnter"
              />
              <button
                type="button"
                class="agenda-cal-btn-pesquisar"
                :disabled="pesquisandoVisitanteEnter || !permissoes.includes(256)"
                aria-label="Pesquisar visitante (agenda do dia ou cadastro)"
                :title="permissoes.includes(256) ? 'Mesma ação do app: Enter ou clique' : 'Busca avançada (check-in/cadastro) requer permissão de recepção'"
                @click="executarPesquisaVisitanteEnter"
              >
                <i v-if="pesquisandoVisitanteEnter" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <i v-else class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div class="agenda-cal-filtro">
            <label class="agenda-cal-filtro-label" for="filtro-anfitriao-cal">Nome do anfitrião</label>
            <BuscaAutocomplete
              input-id="filtro-anfitriao-cal"
              v-model="filtroNomeAnfitriaoCal"
              :options="anfitriaoOpcoesAutocomplete"
              :label-fn="nomeExibicaoAnfitriao"
              :meta-fn="metaAnfitriaoAutocomplete"
              input-class="agenda-cal-filtro-input"
              empty-label="Todos"
              placeholder="Buscar colaborador…"
              :loading="colaboradoresAnfitriaoCarregando"
              loading-label="Carregando colaboradores…"
              :disabled="colaboradoresAnfitriaoCarregando"
            />
          </div>
          <button type="button" class="agenda-cal-limpar-filtros" @click="limparFiltrosCalendario">Limpar
            filtros</button>
        </div>
        <div class="agenda-cal-nav-centro">
          <button type="button" class="agenda-cal-nav-btn" aria-label="Mês anterior" @click="mesAnterior">
            <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
          </button>
          <h3 class="agenda-cal-titulo-mes">{{ tituloMesCalendario }}</h3>
          <button type="button" class="agenda-cal-nav-btn" aria-label="Próximo mês" @click="mesProximo">
            <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
          </button>
          <button type="button" class="agenda-cal-hoje" @click="irMesAtual">Hoje</button>
        </div>
        <div class="agenda-cal-dias-semana">
          <span v-for="abbr in diasSemanaAbbr" :key="abbr" class="agenda-cal-dia-semana">{{ abbr }}</span>
        </div>
        <div class="agenda-cal-grid">
          <div v-for="(cel, idx) in celulasCalendario" :key="cel.ymd + '-' + idx" class="agenda-cal-cel" :class="{
            'agenda-cal-cel--fora': cel.foraMes,
            'agenda-cal-cel--hoje': cel.ymd === hojeYmd,
            'agenda-cal-cel--selecionado': cel.ymd === dataSelecionada,
          }" tabindex="0" :aria-label="'Dia ' + cel.dia + '. Clique para selecionar. Use o botão + para nova visita.'"
            @click="onClicarCelulaDia(cel)" @keydown.enter.prevent="onClicarCelulaDia(cel)"
            @keydown.space.prevent="onClicarCelulaDia(cel)">
            <div class="agenda-cal-cel-topo">
              <span class="agenda-cal-cel-num" aria-hidden="true">{{ cel.dia }}</span>
              <button
                type="button"
                class="agenda-cal-cel-btn-mais"
                :aria-label="'Adicionar visita em ' + formatarDataCurtaParaAria(cel.ymd)"
                title="Adicionar visita neste dia"
                @click.stop="adicionarVisitaNoDia(cel)"
              >
                <i class="fa-solid fa-plus" aria-hidden="true"></i>
              </button>
            </div>
            <ul class="agenda-cal-cel-lista" @click.stop>
              <li v-for="v in visitasResumoNoDia(cel.ymd)"
                :key="v.agendaItemKey || v.id + '-' + (v.visitanteIndex ?? 0)" class="agenda-cal-cel-item"
                :title="tituloVisitaCalendario(v)" @click.stop="abrirDadosCompletosVisita(v.id)">
                <span class="agenda-cal-cel-item-nome">{{ textoVisitante(v) }}</span>
              </li>
              <li v-if="contagemVisitasNoDia(cel.ymd) > 4" class="agenda-cal-cel-mais fonte-menor"> +{{
                contagemVisitasNoDia(cel.ymd) - 4 }}… </li>
            </ul>
          </div>
        </div>
        <div v-if="carregandoMes" class="agenda-cal-loading fonte-menor fonte-fraca">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Atualizando calendário…
        </div>
      </div>
    </div>
    <div v-if="permissoes.includes(256)" class="margem container agenda-link-checkin-wrap">
      <router-link
        class="agenda-link-checkin-dia"
        :to="{ path: '/acesso/checkin-dia', query: { data: dataSelecionada } }"
      >
        <i class="fa-solid fa-clipboard-check" aria-hidden="true"></i>
        Visitas do dia — check-in e saída
      </router-link>
    </div>
    <!-- Cadastro de visita (mesmo fluxo da tela /solicitacao-visita/novo) -->
    <Transition name="agenda-modal-fade">
      <div
        v-if="modalCadastroVisitaAberto"
        class="modal-overlay agenda-modal-cadastro"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-cadastro-visita-titulo"
        aria-describedby="modal-cadastro-visita-desc"
      >
        <div class="modal-backdrop" @click="fecharModalCadastro"></div>
        <div class="modal-sheet modal-sheet--cadastro-visita" @click.stop>
          <header class="agenda-modal-cadastro-topo">
            <div class="agenda-modal-cadastro-titulo-grupo">
              <span class="agenda-modal-cadastro-icone" aria-hidden="true">
                <i class="fa-solid fa-calendar-plus"></i>
              </span>
              <div class="agenda-modal-cadastro-textos">
                <h3 id="modal-cadastro-visita-titulo" class="agenda-modal-cadastro-titulo">Cadastrar visita</h3>
                <p id="modal-cadastro-visita-desc" class="agenda-modal-cadastro-sub">{{ subtituloModalCadastro }}</p>
              </div>
            </div>
            <button type="button" class="agenda-modal-cadastro-fechar" aria-label="Fechar" @click="fecharModalCadastro">
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </header>
          <div class="agenda-modal-cadastro-corpo">
            <SolicitacaoVisitaView
              :key="cadastroVisitaKey"
              em-modal
              :modo-recepcao-agenda="permissoes.includes(256)"
              :data-visita-inicial="dataModalCadastro"
              @fechar="fecharModalCadastro"
            />
          </div>
        </div>
      </div>
    </Transition>
    <!-- Pesquisa Enter: várias visitas no dia -->
    <div
      v-if="modalEscolhaAgendaBusca"
      class="modal-overlay agenda-modal-busca-z"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-busca-agenda-titulo"
    >
      <div class="modal-backdrop" @click="modalEscolhaAgendaBusca = false"></div>
      <div class="modal-sheet modal-sheet--lista-escolha">
        <h3 id="modal-busca-agenda-titulo" class="modal-titulo-busca">Escolha a visita</h3>
        <p class="modal-sub-busca fonte-menor fonte-fraca">Várias visitas combinam com a pesquisa neste dia.</p>
        <ul class="modal-lista-busca">
          <li
            v-for="item in opcoesAgendaPesquisaBusca"
            :key="item.agendaItemKey || item.id + '-' + (item.visitanteIndex ?? 0)"
            class="modal-item-busca"
            @click="selecionarAgendaBuscaItem(item)"
          >
            <i class="fa-regular fa-calendar-check modal-item-icone-busca"></i>
            <div class="modal-item-textos-busca">
              <span class="modal-item-nome-busca">{{ textoVisitante(item) }}</span>
              <span class="modal-item-meta-busca">{{ metaLinhaAgendaBusca(item) }}</span>
            </div>
            <i class="fa-solid fa-chevron-right modal-item-seta-busca"></i>
          </li>
        </ul>
        <button type="button" class="modal-fechar-busca" @click="modalEscolhaAgendaBusca = false">Fechar</button>
      </div>
    </div>
    <!-- Pesquisa Enter: vários cadastros de visitante -->
    <div
      v-if="modalEscolhaCadastroBusca"
      class="modal-overlay agenda-modal-busca-z"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-busca-cad-titulo"
    >
      <div class="modal-backdrop" @click="modalEscolhaCadastroBusca = false"></div>
      <div class="modal-sheet modal-sheet--lista-escolha">
        <h3 id="modal-busca-cad-titulo" class="modal-titulo-busca">Escolha o visitante</h3>
        <p class="modal-sub-busca fonte-menor fonte-fraca">Vários cadastros encontrados — selecione para nova visita.</p>
        <ul class="modal-lista-busca">
          <li
            v-for="v in opcoesCadastroPesquisaBusca"
            :key="'cad-busca-' + v.id"
            class="modal-item-busca"
            @click="selecionarCadastroBuscaItem(v)"
          >
            <i class="fa-regular fa-user modal-item-icone-busca"></i>
            <div class="modal-item-textos-busca">
              <span class="modal-item-nome-busca">{{ v.nomeCompleto }}</span>
              <span class="modal-item-meta-busca">{{ metaLinhaCadastroBuscaItem(v) }}</span>
            </div>
            <i class="fa-solid fa-chevron-right modal-item-seta-busca"></i>
          </li>
        </ul>
        <button type="button" class="modal-fechar-busca" @click="modalEscolhaCadastroBusca = false">Fechar</button>
      </div>
    </div>
  </section>
</template>
<script>
import { createToaster } from "@meforma/vue-toaster";
import solicitacaoVisitaService from "@/services/solicitacao-visita-service";
import setorService from "@/services/setor-service";
import { listarSugestoesAnfitriao, nomeExibicaoAnfitriao } from "@/services/anfitriao-service";
import { rotuloSetorSelect, estiloOptionSetor } from "@/utils/setor-hierarquia";
import visitanteService from "@/services/visitante-service";
import { gravarPrefillVisitanteNovaVisita } from "@/utils/acesso-prefill-visitante";
import { linhaDocumentosVisitante } from "@/config/visitante-documentos";
import { itemCorrespondePesquisaVisitanteMobile } from "@/utils/agenda-busca-visitante";
import { listarYmdIntervaloVisita, visitaIncluiDia } from "@/utils/visita-data";
import BuscaAutocomplete from "@/components/BuscaAutocomplete/BuscaAutocomplete.vue";
import SolicitacaoVisitaView from "./SolicitacaoVisitaView.vue";
import { getPermissao } from "@/services/permissaoService";

const toaster = createToaster({ position: "top-right" });

import { textoStatus as textoStatusConfig } from "@/config/status-visita";

const DIAS_SEMANA_ABBR = ["SEG", "TER", "QUA", "QUI", "SEX", "SÁB", "DOM"];

function ymdDeDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default {
  name: "AgendaVisitasView",

  components: { SolicitacaoVisitaView, BuscaAutocomplete },

  data() {
    const d = new Date();
    const hoje = ymdDeDate(d);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return {
      dataSelecionada: hoje,
      mesCalendarioYm: ym,
      visitasDoMes: [],
      carregandoMes: false,
      modalCadastroVisitaAberto: false,
      dataModalCadastro: "",
      cadastroVisitaKey: 0,
      diasSemanaAbbr: DIAS_SEMANA_ABBR,
      filtroSetorNome: "",
      filtroNomeVisitanteCal: "",
      filtroNomeAnfitriaoCal: "",
      /** Sugestões de anfitrião (`GET usuario/acesso/anfitrioes`). */
      colaboradoresAnfitriaoLista: [],
      colaboradoresAnfitriaoCarregando: false,
      setoresLista: [],
      setoresCarregando: false,
      /** Pesquisa Enter (paridade com app mobile) */
      modalEscolhaAgendaBusca: false,
      opcoesAgendaPesquisaBusca: [],
      modalEscolhaCadastroBusca: false,
      opcoesCadastroPesquisaBusca: [],
      pesquisandoVisitanteEnter: false,
      permissoes: [],
    };
  },

  computed: {
    hojeYmd() {
      return ymdDeDate(new Date());
    },
    tituloMesCalendario() {
      const [y, m] = String(this.mesCalendarioYm || "").split("-").map((x) => parseInt(x, 10));
      if (!Number.isFinite(y) || !Number.isFinite(m)) return "—";
      const dt = new Date(y, m - 1, 1);
      const s = dt.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    /** Colaboradores da API + anfitriões que aparecem no mês mas não estão na lista. */
    anfitriaoOpcoesAutocomplete() {
      const fromApi = Array.isArray(this.colaboradoresAnfitriaoLista) ? [...this.colaboradoresAnfitriaoLista] : [];
      const seen = new Set(
        fromApi.map((x) => nomeExibicaoAnfitriao(x).trim().toLowerCase()).filter(Boolean)
      );
      const extras = [];
      for (const v of this.visitasDoMes || []) {
        const n = String(v.nomeAnfitriao || "").trim();
        if (!n) continue;
        const k = n.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        extras.push({
          id: 0,
          usuario_nome: n,
          nome_completo: n,
          setor_nome: null,
        });
      }
      extras.sort((a, b) => nomeExibicaoAnfitriao(a).localeCompare(nomeExibicaoAnfitriao(b), "pt-BR"));
      return [...fromApi, ...extras];
    },
    visitasDoMesFiltradas() {
      let list = Array.isArray(this.visitasDoMes) ? [...this.visitasDoMes] : [];
      const setor = String(this.filtroSetorNome || "").trim();
      const fvRaw = String(this.filtroNomeVisitanteCal || "").trim();
      const fa = String(this.filtroNomeAnfitriaoCal || "").trim().toLowerCase();
      if (setor) {
        list = list.filter((v) => String(v.areaSetorVisitado || "").trim() === setor);
      }
      if (fvRaw) {
        list = list.filter((v) => this.itemCombinaComPesquisa(v, fvRaw));
      }
      if (fa) {
        list = list.filter((v) => String(v.nomeAnfitriao || "").trim().toLowerCase() === fa);
      }
      return list;
    },
    visitasPorDiaMap() {
      const map = {};
      const lista = Array.isArray(this.visitasDoMesFiltradas) ? this.visitasDoMesFiltradas : [];
      const chaveHorario = (v) => {
        const h = v.horarioEntrada;
        if (!h) return "99:99";
        const s = String(h).trim().slice(0, 5);
        const parts = s.split(":");
        if (parts.length < 2) return s;
        const hh = String(parseInt(parts[0], 10) || 0).padStart(2, "0");
        const mm = String(parseInt(parts[1], 10) || 0).padStart(2, "0");
        return `${hh}:${mm}`;
      };
      const pushNoDia = (ymd, v) => {
        if (!ymd) return;
        const key = String(ymd).slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
        if (!map[key]) map[key] = [];
        map[key].push(v);
      };
      for (const v of lista) {
        if (v.agendaDiaYmd) {
          pushNoDia(v.agendaDiaYmd, v);
          continue;
        }
        const dias = listarYmdIntervaloVisita(v.dataVisita, v.dataVisitaFim);
        if (dias.length > 1) {
          const baseKey = v.agendaItemKey || `${v.id}_${v.visitanteIndex ?? 0}`;
          for (const ymd of dias) {
            pushNoDia(ymd, {
              ...v,
              agendaDiaYmd: ymd,
              agendaItemKey: `${baseKey}@${ymd}`,
            });
          }
          continue;
        }
        pushNoDia(dias[0] ?? v.dataVisita, v);
      }
      Object.keys(map).forEach((k) => {
        map[k].sort((a, b) => chaveHorario(a).localeCompare(chaveHorario(b)));
      });
      return map;
    },
    celulasCalendario() {
      const ym = String(this.mesCalendarioYm || "").trim();
      const parts = ym.split("-");
      if (parts.length < 2) return [];
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return [];
      const first = new Date(y, m - 1, 1);
      const diasNoMes = new Date(y, m, 0).getDate();
      const dowDom = first.getDay();
      const padSegunda = (dowDom + 6) % 7;
      const out = [];
      const prevLast = new Date(y, m - 1, 0).getDate();
      for (let i = 0; i < padSegunda; i++) {
        const dia = prevLast - padSegunda + i + 1;
        const pm = m === 1 ? 12 : m - 1;
        const py = m === 1 ? y - 1 : y;
        const ymd = `${py}-${String(pm).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
        out.push({ dia, ymd, foraMes: true });
      }
      for (let d = 1; d <= diasNoMes; d++) {
        const ymd = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        out.push({ dia: d, ymd, foraMes: false });
      }
      let nd = 1;
      const nm = m === 12 ? 1 : m + 1;
      const ny = m === 12 ? y + 1 : y;
      while (out.length < 42) {
        const ymd = `${ny}-${String(nm).padStart(2, "0")}-${String(nd).padStart(2, "0")}`;
        out.push({ dia: nd, ymd, foraMes: true });
        nd++;
      }
      return out;
    },
    subtituloModalCadastro() {
      const raw = this.dataModalCadastro;
      if (!raw || String(raw).trim().length < 10) {
        return "Nova solicitação para a agenda.";
      }
      const iso = String(raw).slice(0, 10);
      const d = new Date(`${iso}T12:00:00`);
      if (Number.isNaN(d.getTime())) {
        return "Nova solicitação para a agenda.";
      }
      const s = d.toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Nova solicitação para a agenda.";
    },
    visitasDoMesFiltradasSemFiltroVisitante() {
      let list = Array.isArray(this.visitasDoMes) ? [...this.visitasDoMes] : [];
      const setor = String(this.filtroSetorNome || "").trim();
      const fa = String(this.filtroNomeAnfitriaoCal || "").trim().toLowerCase();
      if (setor) {
        list = list.filter((v) => String(v.areaSetorVisitado || "").trim() === setor);
      }
      if (fa) {
        list = list.filter((v) => String(v.nomeAnfitriao || "").trim().toLowerCase() === fa);
      }
      return list;
    },
    visitasNoDiaSelecionadoParaBuscaEnter() {
      const ymd = String(this.dataSelecionada || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return [];
      return this.visitasDoMesFiltradasSemFiltroVisitante.filter((v) => {
        const d = v.agendaDiaYmd ?? v.dataVisita;
        if (!d) return false;
        if (v.agendaDiaYmd) return String(v.agendaDiaYmd).slice(0, 10) === ymd;
        return visitaIncluiDia(v.dataVisita, v.dataVisitaFim, ymd);
      }).filter((v) => !v.cancelada);
    },
  },

  async created() {
    this.permissoes = await getPermissao();
    this.carregarMes();
    this.carregarSetoresAgenda();
    this.carregarColaboradoresAnfitriaoSelect();
  },

  methods: {
    rotuloSetorSelect,
    estiloOptionSetor,
    nomeExibicaoAnfitriao,

    metaAnfitriaoAutocomplete(item) {
      const s = item?.setor_nome?.trim();
      return s ? `Setor: ${s}` : "";
    },

    async carregarSetoresAgenda() {
      this.setoresCarregando = true;
      try {
        this.setoresLista = await setorService.obterListaEstruturaHierarquica();
      } catch (e) {
        console.error(e);
        this.setoresLista = [];
      } finally {
        this.setoresCarregando = false;
      }
    },
    async carregarColaboradoresAnfitriaoSelect() {
      this.colaboradoresAnfitriaoCarregando = true;
      try {
        this.colaboradoresAnfitriaoLista = await listarSugestoesAnfitriao();
      } catch (e) {
        console.error(e);
        this.colaboradoresAnfitriaoLista = [];
        toaster.show("Não foi possível carregar a lista de colaboradores para o filtro.", { type: "warning" });
      } finally {
        this.colaboradoresAnfitriaoCarregando = false;
      }
    },
    limparFiltrosCalendario() {
      this.filtroSetorNome = "";
      this.filtroNomeVisitanteCal = "";
      this.filtroNomeAnfitriaoCal = "";
    },
    mesAnterior() {
      const [y, m] = String(this.mesCalendarioYm || "").split("-").map((x) => parseInt(x, 10));
      if (!Number.isFinite(y) || !Number.isFinite(m)) return;
      const firstPrev = new Date(y, m - 2, 1);
      this.mesCalendarioYm = `${firstPrev.getFullYear()}-${String(firstPrev.getMonth() + 1).padStart(2, "0")}`;
      this.carregarMes();
    },
    mesProximo() {
      const [y, m] = String(this.mesCalendarioYm || "").split("-").map((x) => parseInt(x, 10));
      if (!Number.isFinite(y) || !Number.isFinite(m)) return;
      const firstNext = new Date(y, m, 1);
      this.mesCalendarioYm = `${firstNext.getFullYear()}-${String(firstNext.getMonth() + 1).padStart(2, "0")}`;
      this.carregarMes();
    },
    irMesAtual() {
      const d = new Date();
      this.mesCalendarioYm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      this.dataSelecionada = ymdDeDate(d);
      this.carregarMes();
    },
    visitasResumoNoDia(ymd) {
      const arr = this.visitasPorDiaMap[ymd] || [];
      return arr.slice(0, 4);
    },
    contagemVisitasNoDia(ymd) {
      return (this.visitasPorDiaMap[ymd] || []).length;
    },
    abrirModalCadastroVisita(ymd) {
      if (!ymd || String(ymd).length < 10) return;
      this.dataModalCadastro = String(ymd).slice(0, 10);
      this.cadastroVisitaKey += 1;
      this.modalCadastroVisitaAberto = true;
    },
    onClicarCelulaDia(cel) {
      if (!cel?.ymd) return;
      this.dataSelecionada = cel.ymd;
      if (cel.foraMes) {
        this.mesCalendarioYm = cel.ymd.slice(0, 7);
        this.carregarMes();
      }
    },
    /** Abre o modal de cadastro para o dia da célula (botão +). */
    adicionarVisitaNoDia(cel) {
      if (!cel?.ymd) return;
      this.dataSelecionada = cel.ymd;
      if (cel.foraMes) {
        this.mesCalendarioYm = cel.ymd.slice(0, 7);
        this.carregarMes();
      }
      this.abrirModalCadastroVisita(cel.ymd);
    },
    formatarDataCurtaParaAria(ymd) {
      if (!ymd || String(ymd).length < 10) return String(ymd || "");
      const [y, m, d] = String(ymd).slice(0, 10).split("-");
      if (!y || !m || !d) return ymd;
      return `${d}/${m}/${y}`;
    },
    fecharModalCadastro() {
      this.modalCadastroVisitaAberto = false;
      this.dataModalCadastro = "";
      this.carregarMes();
    },
    async carregarMes() {
      this.carregandoMes = true;
      try {
        const lista = await solicitacaoVisitaService.listarAgendaMes(this.mesCalendarioYm);
        this.visitasDoMes = Array.isArray(lista) ? lista : [];
      } catch (err) {
        console.error(err);
        this.visitasDoMes = [];
        toaster.show("Erro ao carregar agenda do mês.", { type: "error" });
      } finally {
        this.carregandoMes = false;
      }
    },
    apenasDigitos(s) {
      return String(s || "").replace(/\D/g, "");
    },

    itemCombinaComPesquisa(v, termoBruto) {
      const t = String(termoBruto || "").trim().toLowerCase();
      if (!t) return false;
      const nome = String(v.nome || "").toLowerCase();
      if (nome.includes(t)) return true;
      if (!this.permissoes.includes(256)) return false;
      const docItem = this.apenasDigitos(v.documentoVisitante);
      const docBusca = this.apenasDigitos(t);
      if (docBusca.length >= 3 && docItem.includes(docBusca)) return true;
      return false;
    },

    textoVisitante(v) {
      return v.nome || "—";
    },
    /** Tooltip nativo (atributo title) nas visitas do calendário mensal. */
    tituloVisitaCalendario(v) {
      if (!v) return "";
      const parts = [];
      const anf = String(v.nomeAnfitriao || "").trim();
      if (anf) parts.push(`Anfitrião(ã): ${anf}`);
      parts.push(`Visitante: ${this.textoVisitante(v)}`);
      if (v.ehVisitaPorPeriodo && v.dataVisita && v.dataVisitaFim) {
        parts.push(`Período: ${this.formatarDataCurtaParaAria(v.dataVisita)} – ${this.formatarDataCurtaParaAria(v.dataVisitaFim)}`);
      }
      const emp = String(v.empresa || "").trim();
      if (emp) parts.push(`Empresa: ${emp}`);
      if (this.permissoes.includes(256)) {
        const hE = this.formatarHorario(v.horarioEntrada);
        const hS = v.horarioSaida ? this.formatarHorario(v.horarioSaida) : "";
        if (hE && hE !== "—") {
          parts.push(hS && hS !== "—" ? `Horário: ${hE} – ${hS}` : `Entrada: ${hE}`);
        }
        const setor = String(v.areaSetorVisitado || "").trim();
        if (setor) parts.push(`Setor: ${setor}`);
      }
      parts.push(`Status: ${this.textoStatus(v.status, v.cancelada)}`);
      return parts.join(" · ");
    },
    formatarHorario(h) {
      if (!h) return "—";
      const s = String(h).trim();
      if (s.length >= 4 && s.length <= 5) return s.slice(0, 5);
      return s || "—";
    },

    textoStatus(s, cancelada) {
      return textoStatusConfig(s, cancelada);
    },

    abrirDadosCompletosVisita(id) {
      if (!id) return;
      this.$router.push({
        name: "DadosCompletosVisita",
        params: { id: String(id) },
        query: { from: "agenda" },
      });
    },

    metaLinhaAgendaBusca(item) {
      const h = this.formatarHorario(item.horarioEntrada);
      const emp = String(item.empresa || "").trim();
      const parts = [];
      if (h && h !== "—") parts.push(`Entrada ${h}`);
      if (emp) parts.push(emp);
      return parts.length ? parts.join(" · ") : "—";
    },

    metaLinhaCadastroBuscaItem(v) {
      const docs = linhaDocumentosVisitante(v);
      const mail = String(v.email || "").trim();
      if (docs !== "Sem documento") return `${docs}${mail ? ` · ${mail}` : ""}`;
      return mail || "—";
    },

    irChecklistItemAgenda(item) {
      if (!item?.id) return;
      const vi = item.visitanteIndex != null ? Number(item.visitanteIndex) : 0;
      const q = Number.isFinite(vi) && vi > 0 ? { vi: String(vi) } : {};
      this.$router.push({ path: `/acesso/checklist/${item.id}`, query: q });
    },

    selecionarAgendaBuscaItem(item) {
      this.modalEscolhaAgendaBusca = false;
      this.irChecklistItemAgenda(item);
    },

    selecionarCadastroBuscaItem(v) {
      this.modalEscolhaCadastroBusca = false;
      gravarPrefillVisitanteNovaVisita({
        nomeCompleto: v.nomeCompleto,
        cpf: v.cpf || v.CPF || "",
        rg: v.rg || v.documentoRgCnh || "",
        documentoRgCnh: v.rg || v.documentoRgCnh || "",
        email: v.email,
        telefoneWhatsapp: v.telefoneWhatsapp,
        visitanteId: v.id > 0 ? v.id : null,
      });
      this.abrirModalCadastroVisita(this.dataSelecionada);
    },

    async executarPesquisaVisitanteEnter() {
      if (!this.permissoes.includes(256)) return;
      const t = String(this.filtroNomeVisitanteCal || "").trim();
      if (t.length < 2) {
        toaster.show("Digite pelo menos 2 caracteres (nome ou documento).", { type: "warning" });
        return;
      }
      const naAgenda = this.visitasNoDiaSelecionadoParaBuscaEnter.filter((v) =>
        itemCorrespondePesquisaVisitanteMobile(v, t)
      );
      if (naAgenda.length === 1) {
        this.irChecklistItemAgenda(naAgenda[0]);
        return;
      }
      if (naAgenda.length > 1) {
        this.opcoesAgendaPesquisaBusca = naAgenda;
        this.modalEscolhaAgendaBusca = true;
        return;
      }
      this.pesquisandoVisitanteEnter = true;
      try {
        const encontrados = await visitanteService.buscarPorNome(t);
        if (encontrados.length === 0) {
          this.abrirModalCadastroVisita(this.dataSelecionada);
          return;
        }
        if (encontrados.length === 1) {
          const v = encontrados[0];
          gravarPrefillVisitanteNovaVisita({
            nomeCompleto: v.nomeCompleto,
            cpf: v.cpf || v.CPF || "",
            rg: v.rg || v.documentoRgCnh || "",
            documentoRgCnh: v.rg || v.documentoRgCnh || "",
            email: v.email,
            telefoneWhatsapp: v.telefoneWhatsapp,
            visitanteId: v.id > 0 ? v.id : null,
          });
          this.abrirModalCadastroVisita(this.dataSelecionada);
          return;
        }
        this.opcoesCadastroPesquisaBusca = encontrados;
        this.modalEscolhaCadastroBusca = true;
      } catch (err) {
        console.error(err);
        toaster.show(err?.response?.data?.message || "Não foi possível pesquisar o visitante.", { type: "error" });
      } finally {
        this.pesquisandoVisitanteEnter = false;
      }
    },
  },
};
</script>
<style scoped>
.agenda-link-checkin-wrap {
  margin-top: 0.35rem;
  margin-bottom: 0.5rem;
}

.agenda-link-checkin-dia {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--cor-primaria-forte);
  text-decoration: none;
}

.agenda-link-checkin-dia:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.75rem, 3vw, 1.5rem);
}

.modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.modal-sheet {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 28rem;
  max-height: min(85vh, 520px);
  display: flex;
  flex-direction: column;
  background: var(--cor-bg);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 1rem 1rem 0.75rem;
}

.modal-sheet--cadastro-visita {
  max-width: min(52rem, calc(100vw - 1.5rem));
  width: 100%;
  max-height: min(92vh, 920px);
  height: min(92vh, 920px);
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  border-radius: 16px;
  border: 1px solid var(--cor-separador);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 24px 48px -12px rgba(0, 0, 0, 0.28),
    0 12px 24px -8px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.agenda-modal-cadastro-topo {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-shrink: 0;
  padding: 1.15rem 1.25rem 1rem;
  background: linear-gradient(180deg, var(--cor-primaria-fraca, rgba(0, 0, 0, 0.04)) 0%, var(--cor-bg) 100%);
  border-bottom: 1px solid var(--cor-separador);
}

.agenda-modal-cadastro-titulo-grupo {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  min-width: 0;
}

.agenda-modal-cadastro-icone {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  background: var(--cor-bg);
  color: var(--cor-primaria-forte);
  font-size: 1.1rem;
  border: 1px solid var(--cor-separador);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.agenda-modal-cadastro-textos {
  min-width: 0;
}

.agenda-modal-cadastro-titulo {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--cor-fonte-forte);
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.agenda-modal-cadastro-sub {
  margin: 0.35rem 0 0 0;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--cor-fonte-fraca);
}

.agenda-modal-cadastro-fechar {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 999px;
  background: var(--cor-bg);
  color: var(--cor-fonte-fraca);
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.agenda-modal-cadastro-fechar:hover {
  background: var(--cor-cinza2, rgba(0, 0, 0, 0.06));
  color: var(--cor-fonte-forte);
  border-color: var(--cor-separador);
}

.agenda-modal-cadastro-fechar:focus-visible {
  outline: 2px solid var(--cor-primaria-forte);
  outline-offset: 2px;
}

.agenda-modal-cadastro-corpo {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 0.75rem 1.15rem 1rem;
  background: var(--cor-bg);
}

.modal-titulo {
  margin: 0 0 0.75rem 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.agenda-modal-fade-enter-active,
.agenda-modal-fade-leave-active {
  transition: opacity 0.22s ease;
}

.agenda-modal-fade-enter-active .modal-sheet,
.agenda-modal-fade-leave-active .modal-sheet {
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease;
}

.agenda-modal-fade-enter-from,
.agenda-modal-fade-leave-to {
  opacity: 0;
}

.agenda-modal-fade-enter-from .modal-sheet,
.agenda-modal-fade-leave-to .modal-sheet {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

/* Calendário mensal (layout tipo “Agendar visita”) */
.agenda-cal-wrap {
  padding: 1.1rem 1.15rem 1.25rem;
  background: var(--cor-bg);
  border: 1px solid var(--cor-separador);
  border-radius: 12px;
}

.agenda-cal-filtros {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1rem;
  margin-bottom: 1.1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--cor-separador);
}

.agenda-cal-filtro {
  flex: 1 1 11rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.agenda-cal-filtro-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--cor-fonte-fraca);
}

.agenda-cal-filtro-select,
.agenda-cal-filtro-input {
  width: 100%;
  padding: 0.45rem 0.55rem;
  border: 1px solid var(--cor-separador);
  border-radius: 8px;
  font-size: 0.88rem;
  background: var(--cor-bg);
  color: var(--cor-fonte);
}

.agenda-cal-limpar-filtros {
  flex: 0 0 auto;
  align-self: center;
  margin-left: auto;
  padding: 0.4rem 0;
  border: none;
  background: none;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--cor-primaria-forte);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.agenda-cal-limpar-filtros:hover {
  color: var(--cor-primaria);
}

.agenda-cal-nav-centro {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem 0.65rem;
  margin-bottom: 0.85rem;
}

.agenda-cal-titulo-mes {
  margin: 0;
  min-width: 10rem;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--cor-fonte);
}

.agenda-cal-nav-btn {
  width: 2.1rem;
  height: 2.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--cor-separador);
  border-radius: 8px;
  background: var(--cor-bg);
  color: var(--cor-fonte);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.agenda-cal-nav-btn:hover {
  border-color: var(--cor-primaria-media);
  color: var(--cor-primaria-forte);
}

.agenda-cal-hoje {
  margin-left: 0.35rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  border: 1px solid var(--cor-separador);
  border-radius: 8px;
  background: var(--cor-bg);
  cursor: pointer;
  color: var(--cor-primaria-forte);
}

.agenda-cal-hoje:hover {
  border-color: var(--cor-primaria-media);
}

.agenda-cal-dias-semana {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.35rem;
  margin-bottom: 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: var(--cor-fonte-fraca);
  text-align: center;
}

.agenda-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.45rem;
  align-items: stretch;
}

.agenda-cal-cel {
  min-width: 0;
  min-height: 5.75rem;
  overflow: hidden;
  background: var(--cor-bg);
  padding: 0.35rem 0.4rem 0.45rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  border: 1px solid var(--cor-separador);
  border-radius: 10px;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}

.agenda-cal-cel:hover {
  border-color: var(--cor-primaria-media);
}

.agenda-cal-cel--fora {
  opacity: 0.42;
}

.agenda-cal-cel--hoje:not(.agenda-cal-cel--selecionado) {
  border-color: var(--cor-primaria-media);
}

.agenda-cal-cel--selecionado {
  background: var(--cor-primaria-fraca);
  border-color: var(--cor-primaria-forte);
  box-shadow: 0 0 0 1px var(--cor-primaria-media);
}

.agenda-cal-cel-topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
  width: 100%;
  margin-bottom: 0.25rem;
  flex-shrink: 0;
}

.agenda-cal-cel-num {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--cor-fonte);
  line-height: 1.1;
  min-width: 0;
}

.agenda-cal-cel-btn-mais {
  flex-shrink: 0;
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--cor-separador);
  border-radius: 6px;
  background: transparent;
  color: var(--cor-fonte-fraca);
  font-size: 0.62rem;
  font-weight: 400;
  cursor: pointer;
  opacity: 0.85;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease, opacity 0.15s ease;
}

.agenda-cal-cel-btn-mais:hover {
  opacity: 1;
  color: var(--cor-primaria-forte);
  border-color: var(--cor-primaria-media);
  background: var(--cor-primaria-fraca);
}

.agenda-cal-cel-btn-mais:focus-visible {
  outline: 1px solid var(--cor-primaria-media);
  outline-offset: 1px;
  opacity: 1;
}

.agenda-cal-cel:hover .agenda-cal-cel-btn-mais {
  opacity: 1;
}

.agenda-cal-cel-lista {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.agenda-cal-cel-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  line-height: 1.2;
  padding: 0.22rem 0.35rem;
  border-radius: 6px;
  background: var(--cor-primaria-fraca);
  color: var(--cor-fonte);
  font-size: 0.72rem;
  font-weight: 500;
  cursor: pointer;
  flex: 0 0 auto;
}

.agenda-cal-cel-item-nome {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agenda-cal-cel-item:hover {
  filter: brightness(0.97);
}

.agenda-cal-cel-mais {
  flex: 0 0 auto;
  color: var(--cor-fonte-fraca);
  font-style: italic;
  padding-left: 0.15rem;
  line-height: 1.2;
}

.agenda-cal-loading {
  margin-top: 0.55rem;
  text-align: center;
}

.modal-botoes-linha {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--cor-separador);
}

.modal-btn-sec,
.modal-btn-pri {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.modal-btn-sec {
  border: 1px solid var(--cor-separador);
  background: transparent;
  color: var(--cor-fonte);
}

.modal-btn-pri {
  border: none;
  background: var(--cor-primaria);
  color: #fff;
}

.agenda-cal-pesquisa-row {
  display: flex;
  align-items: stretch;
  gap: 0.35rem;
}

.agenda-cal-filtro-input--com-botao {
  flex: 1;
  min-width: 0;
}

.agenda-cal-btn-pesquisar {
  flex-shrink: 0;
  width: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--cor-separador);
  border-radius: 8px;
  background: var(--cor-primaria);
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s;
}

.agenda-cal-btn-pesquisar:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.agenda-modal-busca-z {
  z-index: 1002;
}

.modal-sheet--lista-escolha {
  max-width: 26rem;
  max-height: min(85vh, 520px);
  display: flex;
  flex-direction: column;
  padding: 1rem 1rem 0.75rem;
}

.modal-titulo-busca {
  margin: 0 0 0.35rem 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.modal-sub-busca {
  margin: 0 0 0.65rem 0;
}

.modal-lista-busca {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  max-height: 55vh;
}

.modal-item-busca {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.5rem;
  border-bottom: 1px solid var(--cor-separador);
  cursor: pointer;
  border-radius: 6px;
}

.modal-item-busca:hover {
  background: var(--cor-primaria-fraca);
}

.modal-item-icone-busca {
  flex-shrink: 0;
  color: var(--cor-primaria-forte);
}

.modal-item-textos-busca {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.modal-item-nome-busca {
  font-weight: 600;
  font-size: 0.95rem;
}

.modal-item-meta-busca {
  font-size: 0.8rem;
  color: var(--cor-fonte-fraca);
}

.modal-item-seta-busca {
  flex-shrink: 0;
  color: var(--cor-primaria-forte);
  font-size: 0.85rem;
}

.modal-fechar-busca {
  margin-top: 0.5rem;
  padding: 0.5rem;
  width: 100%;
  border: none;
  background: transparent;
  color: var(--cor-primaria-forte);
  font-weight: 600;
  cursor: pointer;
}

@media (max-width: 639px) {
  .agenda-cal-filtros {
    flex-direction: column;
    align-items: stretch;
  }

  .agenda-cal-limpar-filtros {
    margin-left: 0;
    align-self: flex-start;
  }

  .agenda-cal-nav-centro {
    justify-content: center;
  }

  .agenda-cal-hoje {
    margin-left: 0;
    width: 100%;
    max-width: 8rem;
  }

  .agenda-cal-grid {
    gap: 0.3rem;
  }

  .agenda-cal-cel {
    min-height: 4.25rem;
    padding: 0.28rem 0.3rem;
  }

  .agenda-cal-cel-item {
    font-size: 0.62rem;
    padding: 0.18rem 0.28rem;
  }
}
</style>
